import React, { useEffect, useState, useRef } from 'react';
import { Button } from './ui/button';
import { CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
interface DashboardShowcaseProps {
  onNavigate?: (page: 'home' | 'fitur' | 'kontak') => void;
}
export function DashboardShowcase({
  onNavigate
}: DashboardShowcaseProps) {
  const {
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const screens = [{
    img: 'https://cdn.magicpatterns.com/uploads/rpHNegJhi9AbiPSYST2Gsx/Screenshot_2026-02-18_at_03-04-11_HT_Group_App.png'
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/g3N3SQqNPHZaSUfQCcyd9R/Screenshot_2026-02-18_at_03-04-27_HT_Group_App.png'
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/aNbDpqbcps6vedG2gitp3A/Screenshot_2026-02-18_at_03-04-44_HT_Group_App.png'
  }];
  const goTo = (idx: number, dir: 'left' | 'right') => {
    if (isAnimating || idx === activeTab) return;
    setDirection(dir);
    setIsAnimating(true);
    setTimeout(() => {
      setActiveTab(idx);
      setIsAnimating(false);
    }, 350);
  };
  const goPrev = () => goTo((activeTab - 1 + screens.length) % screens.length, 'left');
  const goNext = () => goTo((activeTab + 1) % screens.length, 'right');
  const resetAutoPlay = () => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      setDirection('right');
      setIsAnimating(true);
      setTimeout(() => {
        setActiveTab(prev => (prev + 1) % screens.length);
        setIsAnimating(false);
      }, 350);
    }, 4000);
  };
  useEffect(() => {
    resetAutoPlay();
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, []);
  return <section className="py-16 md:py-24 lg:py-40 bg-white overflow-hidden" data-id="element-41">
      <div className="container mx-auto px-4 sm:px-6 md:px-8" data-id="element-42">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16" data-id="element-43">
          <p className="text-forest-500 font-semibold text-xs uppercase tracking-[0.2em] mb-4" data-id="element-44">
            {t.dashboard.label}
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-forest-900 mb-4 tracking-tight" data-id="element-45">
            {t.dashboard.headline}
          </h2>
          <p className="text-forest-600 text-xl font-light leading-relaxed" data-id="element-46">
            {t.dashboard.subtitle}
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex gap-2 mb-6 flex-wrap justify-center" data-id="element-47">
          {t.dashboard.tabs.map((tab, idx) => <button key={idx} onClick={() => {
          goTo(idx, idx > activeTab ? 'right' : 'left');
          resetAutoPlay();
        }} className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${activeTab === idx ? 'bg-brand-green text-white shadow-sm' : 'bg-forest-50 text-forest-600 hover:bg-forest-100'}`} data-id="element-48">
              {tab}
              <span className={`ml-1.5 text-xs font-normal hidden sm:inline ${activeTab === idx ? 'text-white/70' : 'text-forest-400'}`} data-id="element-49">
                {t.dashboard.tabDescs[idx]}
              </span>
            </button>)}
        </div>

        {/* Screenshot Slider */}
        <div className="relative" data-id="element-50">
          <div className="absolute inset-6 bg-brand-green/8 rounded-3xl blur-3xl pointer-events-none" data-id="element-51" />

          <div className="relative bg-white border border-gray-200 rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl overflow-hidden" data-id="element-52">
            {/* Browser Chrome */}
            <div className="h-9 sm:h-12 bg-gray-50 border-b border-gray-200 flex items-center px-3 sm:px-6 gap-2 flex-shrink-0" data-id="element-53">
              <div className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 rounded-full bg-red-400/70" data-id="element-54" />
              <div className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 rounded-full bg-yellow-400/70" data-id="element-55" />
              <div className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 rounded-full bg-green-400/70" data-id="element-56" />
              <div className="ml-3 flex-1 bg-gray-200/80 rounded-md h-5 items-center px-3 max-w-sm hidden sm:flex" data-id="element-57">
                <span className="text-xs text-gray-400 font-medium" data-id="element-58">
                  app.sawit-net.com/dashboard
                </span>
              </div>
              <div className="ml-auto flex items-center gap-1.5 sm:gap-2" data-id="element-59">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500 animate-pulse" data-id="element-60" />
                <span className="text-[10px] sm:text-xs text-gray-400 font-medium" data-id="element-61">
                  Live
                </span>
              </div>
            </div>

            {/* Sliding Image Area */}
            <div className="relative overflow-hidden" data-id="element-62">
              <div style={{
              transform: isAnimating ? `translateX(${direction === 'right' ? '-4%' : '4%'})` : 'translateX(0)',
              opacity: isAnimating ? 0 : 1,
              transition: 'transform 350ms ease-in-out, opacity 350ms ease-in-out'
            }} data-id="element-63">
                <img src={screens[activeTab].img} alt={`Sawit-NET ${t.dashboard.tabs[activeTab]}`} className="w-full block" data-id="element-64" />
              </div>
              <button onClick={() => {
              goPrev();
              resetAutoPlay();
            }} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/90 hover:bg-white border border-gray-200 rounded-full shadow-lg flex items-center justify-center text-gray-700 hover:text-gray-900 transition-all duration-200 z-10" data-id="element-65">
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-66" />
              </button>
              <button onClick={() => {
              goNext();
              resetAutoPlay();
            }} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/90 hover:bg-white border border-gray-200 rounded-full shadow-lg flex items-center justify-center text-gray-700 hover:text-gray-900 transition-all duration-200 z-10" data-id="element-67">
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-68" />
              </button>
            </div>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-2 mt-4 sm:mt-5" data-id="element-69">
            {screens.map((_, idx) => <button key={idx} onClick={() => {
            goTo(idx, idx > activeTab ? 'right' : 'left');
            resetAutoPlay();
          }} className={`transition-all duration-300 rounded-full ${activeTab === idx ? 'w-8 h-2.5 bg-brand-green' : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'}`} data-id="element-70" />)}
          </div>
        </div>

        {/* Feature checklist */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-8 sm:mt-10" data-id="element-71">
          {t.dashboard.features.map((item, idx) => <div key={idx} className="flex items-start gap-3 p-4 bg-forest-50 rounded-xl border border-forest-100" data-id="element-72">
              <div className="w-6 h-6 bg-forest-100 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-73">
                <CheckCircle2 className="w-4 h-4 text-brand-green" data-id="element-74" />
              </div>
              <div data-id="element-75">
                <h4 className="font-semibold text-forest-900 text-sm" data-id="element-76">
                  {item.title}
                </h4>
                <p className="text-xs text-forest-600 mt-0.5" data-id="element-77">{item.desc}</p>
              </div>
            </div>)}
        </div>

        <div className="text-center mt-8" data-id="element-78">
          <Button onClick={() => onNavigate?.('fitur')} className="bg-brand-green hover:bg-forest-600 text-white font-semibold h-12 px-8 rounded-lg shadow-sm hover:shadow-md transition-all" data-id="element-79">
            {t.dashboard.cta}
          </Button>
        </div>
      </div>
    </section>;
}